import { Combobox, ComboboxInput, ComboboxPopover } from '@reach/combobox';
import { ListboxInput, ListboxButton, ListboxList } from '@reach/listbox';
import { positionDefault } from '@reach/popover';
import React__default, { useRef, useCallback, useState, useMemo, Children, useEffect } from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { useQueryChildComponents } from '../base/SystemComponent.js';
import { Button } from '../button/Button.js';
import { Chip } from '../chip/Chip.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { DEPRECATED_ComboboxOption } from '../fields/DEPRECATED_commonCombobox.js';
import { SelectPopover, SELECT_OPTION_NO_VALUE } from '../fields/DEPRECATED_commonSelect.js';
import { Stack } from '../layout/Stack.js';
import { useCheckModal } from '../modal/Modal.js';
import { setPortalLayer } from '../styles/stylePropTypes.js';
import { DEPRECATED_ComboboxList } from './DEPRECATED_ComboboxCommon.js';
import { CSSDefaultInput, InnerInputLabel } from './InputCommon.js';

const MAX_COMBOBOX_SEARCH_RESULTS = 50;
const useInputComboboxOptions = (children, onBlur, onInputChange, /**
 * Reset the search term to empty string when the combobox is initialised, blurred or an option is selected.
 * The corresponding field's value will not be cleared when the search text is cleared.
 */ resetSearchTermOnChange)=>{
    const [value, setValue] = useState("");
    // create a dictionary of possible value/labels.
    // this is needed as the standard combobox uses a single value for search and selection. This doesn't work
    // for when the value in formik will need to be an ID or something similar with a descriptive label.
    const { optionLabels , optionValues  } = useMemo(()=>{
        const labels = {};
        const values = {};
        Children.forEach(children, (child)=>{
            if (child.props?.label !== undefined) {
                labels[child.props.value] = child.props.label;
                values[child.props.label] = child.props.value;
            }
        });
        return {
            optionLabels: labels,
            optionValues: values
        };
    }, [
        children
    ]);
    // get selected label from a value
    const getSelectedLabel = useCallback((value)=>optionLabels[value] || "", [
        optionLabels
    ]);
    const getSelectedValue = useCallback((label)=>optionValues[label] || "", [
        optionValues
    ]);
    // currently selected label
    const selectedLabel = useMemo(()=>getSelectedLabel(value), [
        value,
        getSelectedLabel
    ]);
    const [searchTerm, setSearchTerm] = useState(resetSearchTermOnChange ? "" : selectedLabel);
    // do a search through provided child values
    const { filteredChildren , searchTotalCount  } = useMemo(()=>{
        const comboChildren = [];
        Children.forEach(children, (child)=>{
            if (!searchTerm || // Show all options if the user is expanding a combobox with an existing value
            searchTerm === getSelectedLabel(value) || child.props.label?.toLocaleLowerCase()?.includes(searchTerm?.toLocaleLowerCase())) {
                comboChildren.push(child);
            }
        });
        return {
            filteredChildren: comboChildren.slice(0, MAX_COMBOBOX_SEARCH_RESULTS),
            searchTotalCount: comboChildren.length
        };
    }, [
        children,
        searchTerm
    ]);
    // Wrap the onChange to perform side effects if needed.
    const handleChange = useCallback((label)=>{
        const updatedValue = optionValues[label] === SELECT_OPTION_NO_VALUE ? "" : getSelectedValue(label);
        setSearchTerm(resetSearchTermOnChange ? "" : label);
        if (onInputChange) {
            onInputChange(updatedValue);
        }
        setValue(updatedValue);
    }, [
        getSelectedValue,
        onInputChange,
        setValue,
        optionValues,
        resetSearchTermOnChange
    ]);
    // Bindings for the search input.
    const handleSearchTermChange = useCallback((event)=>{
        setSearchTerm(event.target.value);
    }, []);
    // When clicking outside the `Combobox` ensure that a valid value is set.
    const handleBlur = useCallback((e)=>{
        if (e.relatedTarget?.hasAttribute("data-hpy-web-combobox-option")) {
            // Click is against an option, don't override the search term
            return;
        }
        if (resetSearchTermOnChange) {
            setSearchTerm("");
        } else {
            const selectedLabel = getSelectedLabel(value);
            setSearchTerm(selectedLabel);
        }
        if (onBlur) {
            onBlur(e);
        }
    }, [
        value,
        getSelectedLabel,
        resetSearchTermOnChange
    ]);
    // Reset the current value if current value is completely removed from search
    useEffect(()=>{
        if (!resetSearchTermOnChange && searchTerm === "" && value !== "") {
            setValue("");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        searchTerm
    ]);
    return {
        filteredChildren,
        handleSearchTermChange,
        handleBlur,
        searchTotalCount,
        searchTerm,
        setSearchTerm,
        handleChange,
        selectedLabel
    };
};
const StyledCombobox = styled(Combobox)`
  position: relative;
  text-align: left;

  & [data-reach-combobox-list] {
    max-height: 14rem;
    overflow: auto;
  }
`;
const MIN_INPUT_WIDTH_REM = 13;
const StyledListboxInput = styled(ListboxInput)`
  width: 100%;
  min-width: ${MIN_INPUT_WIDTH_REM}rem;

  &[data-state="expanded"] [${Chip.dataId}-actions] {
    display: none;
  }
`;
const StyledComboboxInput = styled(ComboboxInput)`
  &[data-reach-combobox-input] {
    ${CSSDefaultInput}
    position: relative;
    display: flex;
    cursor: pointer;
    padding: 1.5rem 2.5rem 0.5rem 1rem;
    align-items: flex-start;
    width: 100%;
    min-width: ${MIN_INPUT_WIDTH_REM}rem;
  }

  ​[data-reach-combobox] &[data-reach-combobox-input][aria-expanded='true'],
  [data-reach-combobox] &[data-reach-combobox-input][aria-expanded='true']:hover,
  [data-reach-combobox] &[data-reach-combobox-input]:focus,
  [data-reach-combobox] &[data-reach-combobox-input][aria-expanded='true']:focus {
    outline: none;
    border-color: ${COLOR_PALETTE.slateGrey40};
  }

  ​&[data-reach-combobox-input][aria-expanded='true'] ~ [data-reach-listbox-arrow] {
    transform: rotate(180deg);
  }
`;
const StyledListBoxArrow = styled.span`
  position: absolute;
  top: 50%;
  margin-top: -2px;
  right: 1rem;
  display: block;
  width: 0px;
  height: 0px;
  content: '';
  transform: rotate(0deg);
  border-top: 5px solid ${COLOR_PALETTE.slateGrey100};
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  transition: transform 0.2s ease 0s, -webkit-transform 0.2s ease 0s;
  pointer-events: none;
`;
const StyledComboboxPopover = styled(ComboboxPopover)`
  &[data-reach-combobox-popover] {
    background-color: white;
    box-shadow: rgba(0, 0, 0, 0.16) 0px 2px 3px 0px;
    border: 1px solid ${COLOR_PALETTE.slateGrey40};
    border-radius: 0.25rem;
    padding: 0;
    ${setPortalLayer()}
  }

  &[data-reach-combobox-popover],
  &[data-reach-combobox-popover]:focus-within {
    box-shadow: rgba(0, 0, 0, 0.16) 0px 2px 3px 0px;
    outline: none;
  }

  &[data-reach-combobox-popover] [data-reach-combobox-list] {
    max-height: 14rem;
    overflow: auto;
  }
`;
const PopoverComboboxInput = styled(ComboboxInput)`
  &[data-reach-combobox-input] {
    ${CSSDefaultInput}
    min-height: 2.5rem;
    height: 2.5rem;
    padding: 0 1rem;
    justify-content: center;
    display: flex;
    font-size: 0.875rem;
    align-items: flex-start;
    width: 100%;
    min-width: ${MIN_INPUT_WIDTH_REM}rem;
    border: none;
    border-bottom: 1px solid ${COLOR_PALETTE.slateGrey40};
    border-radius: 0;
    background: none;
    color: inherit;
  }

  &[data-reach-combobox-input][aria-expanded='true'],
  &[data-reach-combobox-input]:focus {
    outline: none;
  }
`;
const ComboboxInputInline = ({ label , onInputChange , onBlur , children , testId , ...props })=>{
    const [optionChildren] = useQueryChildComponents(children, [
        DEPRECATED_ComboboxOption
    ]);
    const { filteredChildren , handleChange , handleSearchTermChange , handleBlur , searchTotalCount , searchTerm  } = useInputComboboxOptions(optionChildren, onBlur, onInputChange);
    return React__default.createElement(Stack, {
        "data-hpy-web-combobox-input": true,
        "data-hpy-web-combobox-input-inline": true,
        ...props,
        ...setTestId("ComboboxInput-" + testId),
        gap: 0.25
    }, React__default.createElement(StyledCombobox, {
        openOnFocus: true,
        onSelect: handleChange
    }, React__default.createElement(StyledComboboxInput, {
        "data-hpy-web-combobox-input-input": true,
        ...setTestId("ComboboxInput-Input-" + testId),
        placeholder: label,
        value: searchTerm,
        selectOnClick: true,
        onChange: handleSearchTermChange,
        onBlur: handleBlur
    }), React__default.createElement(InnerInputLabel, {
        "data-hpy-web-combobox-input-input-inner-label": true,
        size: "small"
    }, label), React__default.createElement(StyledListBoxArrow, null, "\xa0"), React__default.createElement(StyledComboboxPopover, {
        ...setTestId("ComboboxInput-Popover-" + testId),
        portal: true
    }, React__default.createElement(DEPRECATED_ComboboxList, {
        totalOptions: searchTotalCount
    }, filteredChildren))));
};
ComboboxInputInline.dataId = "data-hpy-web-combobox-input-inline";
ComboboxInputInline.displayName = "ComboboxInputInline";
const ComboboxInputAs = ({ label , onInputChange , onBlur , children , testId , className , as , ...props })=>{
    const [optionChildren, asChildren] = useQueryChildComponents(children, [
        DEPRECATED_ComboboxOption
    ]);
    const { filteredChildren , handleChange , handleSearchTermChange , handleBlur , searchTotalCount , searchTerm , selectedLabel , setSearchTerm  } = useInputComboboxOptions(optionChildren, onBlur, onInputChange, true);
    const isModal = useCheckModal();
    const comboboxInputRef = useRef();
    const chipButtonRef = useRef();
    const handleChangeAndCloseCombobox = useCallback((e)=>{
        handleChange(e);
        // Giving the button focus hides the select dropdown
        chipButtonRef.current?.focus();
    }, [
        handleChange
    ]);
    const handleButtonClick = useCallback(()=>{
        // This click event will trigger display of the combobox - until it is displayed its children
        // cannot receive focus. Wait briefly and then hand focus to the input.
        setTimeout(()=>{
            comboboxInputRef.current && comboboxInputRef.current.focus();
        }, 100);
    }, [
        comboboxInputRef
    ]);
    return React__default.createElement(StyledListboxInput, {
        ...setTestId("ComboboxInput-Listbox-" + testId)
    }, React__default.createElement(ListboxButton, {
        as: as,
        ...setTestId("ComboboxInput-Button-" + testId),
        ...props,
        ref: chipButtonRef,
        label: label,
        sublabel: selectedLabel,
        testId: "ComboboxInput-Button-" + testId,
        onClick: handleButtonClick
    }, asChildren), React__default.createElement(SelectPopover, {
        position: positionDefault,
        portal: !isModal
    }, React__default.createElement(ListboxList, null, React__default.createElement(StyledCombobox, {
        openOnFocus: true,
        onSelect: handleChangeAndCloseCombobox
    }, React__default.createElement(PopoverComboboxInput, {
        ...setTestId("ComboboxInput-PopoverInput-" + testId),
        ref: comboboxInputRef,
        value: searchTerm,
        selectOnClick: true,
        autocomplete: false,
        onChange: handleSearchTermChange,
        onBlur: handleBlur,
        // ListBox's onKeyDown event listener intercepts the "space" character, so we need to override its behaviour in the input
        onKeyDown: (event)=>{
            if (event.key === " ") {
                setSearchTerm(searchTerm + " ");
            }
        }
    }), React__default.createElement(DEPRECATED_ComboboxList, {
        totalOptions: searchTotalCount
    }, filteredChildren)))));
};
ComboboxInputAs.dataId = "data-hpy-web-combobox-input-as";
ComboboxInputAs.displayName = "ComboboxInputAs";
/**
 * Combobox Input.
 * Accepts `ComboboxOptions` as child components. If a 'deselected' option is required, it
 * must be manually added to options.
 *
 * @deprecated - use `SelectInput` with an onSearch handler instead
 *
 * @example
 * <ComboboxInput name="chicken">
 *  <ComboboxOption label="" value={SELECT_OPTION_NO_VALUE} />
 *  <ComboboxOption label="Egg" value="egg" />
 *  <ComboboxOption label="Duck" value="duck" />
 * </ComboboxInput>
 */ const DEPRECATED_ComboboxInput = ({ children , testId ="X" , ...props })=>{
    if (hasInline(props)) {
        return React__default.createElement(ComboboxInputInline, {
            ...props,
            testId: testId
        }, children);
    }
    if (hasChip(props)) {
        return React__default.createElement(ComboboxInputAs, {
            as: Chip,
            ...props,
            testId: testId
        }, children);
    }
    if (!hasChip(props)) {
        return React__default.createElement(ComboboxInputAs, {
            as: Button,
            ...props,
            testId: testId
        }, children);
    }
    return null;
};
DEPRECATED_ComboboxInput.dataId = "data-hpy-web-combobox-input";
DEPRECATED_ComboboxInput.displayName = "ComboboxInput";
const hasInline = (props)=>!props.as;
const hasChip = (props)=>props?.as?.dataId === Chip.dataId;

export { DEPRECATED_ComboboxInput, useInputComboboxOptions };
